import BarekamText from "../../../pages/components/text";
import { Message, MessageSections } from "../../../pages/components/message/message";
import React, { useEffect, useState } from "react";
import {
    BalanceActionTypes,
    GenericActionTypes,
    InWarehouseActionTypes,
    MessagesManagementActionTypes,
    ReadyForShipmentActionTypes,
    StaticDataActionTypes,
    WarehouseManagementActionTypes
} from "../action-types.enum";
import { StandardDialog, StandardDialogFooter } from "../../../pages/components/dialogs/standard-dialog/standard-dialog";
import { ActionKeys } from "../action-keys.enum";
import BulkAction from "./blocks/bulk-action";
import { OrderModel } from "../../../pages/shared/models/order/order-item.model";
import ScanForSearch from "./blocks/scan-for-search";
import ScanForExport from "./blocks/scan-for-export";
import NewGroup from "./blocks/new-group";
import AddToGroup from "./blocks/add-to-group";
import { OrderGroupModel } from "../../../pages/shared/models/order/orderGroupModel";
import { ToastContainer } from "react-toastify";
import { History, HistoryPopupType } from "./blocks/history";
import { actionsWithPreOpenedAdditionalDialogs } from "../../../constants";
import { LocationModel } from "../../../pages/shared/models/location.model";
import { Breakpoint } from "@mui/system";
import BulkLocate from "./blocks/bulk-locate";
import SelectLocationAndBulkLocate from "./blocks/select-location-and-bulk-locate";
import { GapContainer } from "../../../pages/components/card/styled";
import NewWarehouse from "./blocks/new-warehouse";
import NewWarehouseWorker from "./blocks/new-warehouse-worker";
import { Discount } from "./blocks/discount";
import NewMessage from "./blocks/new-message";
import EditStaticData, { StaticDataDialogProps } from "./blocks/static-data/edit-static-data";
import DeleteStaticData from "./blocks/static-data/delete-static-data";
import { AnnounceOrder } from "./blocks/announce-order";

export const ActionDialog = ({ action, orderStateId, predefinedOrders, orderGroup, open, closePopup, historyDialogProps, location, staticDataDialogProps, messageSection = MessageSections.ORDERS }: ActionDialogProps) => {
    const [isReady, setIsReady] = useState(false);
    const [orders, setOrders] = useState(predefinedOrders);
    const [isAdditionalPopupOpen, setIsAdditionalPopupOpen] = useState(false);
    const [preSelectedLocation, setPreSelectedLocation] = useState<LocationModel | undefined>(location);

    useEffect(() => {
        if (open) {
            setOrders(orderGroup?.orders || predefinedOrders)
        } else {
            setOrders([]);
            setIsAdditionalPopupOpen(false);
            setPreSelectedLocation(undefined);
            setIsReady(false);
        }
    }, [open])

    useEffect(() => {
        setIsAdditionalPopupOpen(actionsWithPreOpenedAdditionalDialogs.includes(action));
    }, [action])

    useEffect(() => {
        if (action === InWarehouseActionTypes.PRE_SELECT_LOCATION_AND_ADD_ORDERS) {
            setPreSelectedLocation(location);
            setIsReady(!!action && !!location)
        } else {
            setIsReady(!!action)
        }
    }, [action, location])

    function getHeaderLabel(): React.ReactElement {
        switch (action) {
            case InWarehouseActionTypes.MARK_AS_READY_FOR_SHIPMENT:
            case InWarehouseActionTypes.NOT_PREDEFINED:
            case InWarehouseActionTypes.CHANGE_STATUS:
            case InWarehouseActionTypes.SELECT_ORDERS_TO_LOCATE:
            case InWarehouseActionTypes.LOCATE:
            case InWarehouseActionTypes.EXPORT:
            case GenericActionTypes.ADD_TO_GROUP:
            case InWarehouseActionTypes.PRE_SELECT_LOCATION_AND_ADD_ORDERS:
            case InWarehouseActionTypes.SELECT_LOCATION_AND_ADD_ORDERS:
            case GenericActionTypes.SCAN_FOR_SEARCH:
                return (
                    <GapContainer style={{ justifyContent: 'space-between' }}>
                        <BarekamText variant={'l'} bold>
                            <Message messageKey={action && ActionKeys[action]} sectionId={messageSection} />
                        </BarekamText>

                        <BarekamText variant={'l'} bold>
                            <Message messageKey={'selected_orders_count'} sectionId={messageSection} />
                            {': '}
                            {orders?.length || 0}
                        </BarekamText>
                    </GapContainer>
                )
            default:
                return (
                    <BarekamText variant={'l'} bold>
                        <Message messageKey={action && ActionKeys[action]} sectionId={messageSection} />
                    </BarekamText>
                )
        }
    }

    function getWidth(): Breakpoint {
        switch (action) {
            case InWarehouseActionTypes.ANNOUNCE:
                return 'md'
            case GenericActionTypes.SEE_LOCATION_HISTORY:
            case GenericActionTypes.SEE_STATUS_HISTORY:
            case GenericActionTypes.SET_DISCOUNT:
            case WarehouseManagementActionTypes.NEW_WAREHOUSE:
            case WarehouseManagementActionTypes.NEW_WAREHOUSE_WORKER:
            case MessagesManagementActionTypes.NEW_MESSAGE:
            case MessagesManagementActionTypes.EDIT_MESSAGE:
            case StaticDataActionTypes.NEW:
            case StaticDataActionTypes.EDIT:
            case BalanceActionTypes.SEE_BONUS_HISTORY:
            case BalanceActionTypes.SEE_BALANCE_HISTORY:
                return 'sm'
            case StaticDataActionTypes.DELETE:
                return 'xs'
            default:
                return 'xl'
        }
    }

    const ActionBlock = () => {
        switch (action) {
            case InWarehouseActionTypes.MARK_AS_READY_FOR_SHIPMENT:
            case InWarehouseActionTypes.NOT_PREDEFINED:
            case InWarehouseActionTypes.CHANGE_STATUS:
            case ReadyForShipmentActionTypes.MARK_AS_SENT:
                return <BulkAction actionType={action}
                    groupData={orderGroup}
                    onCancel={() => closePopup()}
                    preSelectedOrders={orders}
                    orderStateId={orderStateId}
                    isAdditionalPopupOpen={isAdditionalPopupOpen}
                    setIsAdditionalPopupOpen={setIsAdditionalPopupOpen}
                    ordersUpdated={setOrders} />
            case InWarehouseActionTypes.READY_FOR_PICKUP:
                return <BulkAction actionType={action}
                    groupData={orderGroup}
                    onCancel={() => closePopup()}
                    preSelectedOrders={orders}
                    orderStateId={orderStateId}
                    isAdditionalPopupOpen={isAdditionalPopupOpen}
                    setIsAdditionalPopupOpen={setIsAdditionalPopupOpen}
                    ordersUpdated={setOrders} />
            case InWarehouseActionTypes.SELECT_ORDERS_TO_LOCATE:
            case InWarehouseActionTypes.LOCATE:
                return <BulkLocate actionType={action}
                    onCancel={() => closePopup()}
                    preSelectedOrders={orders}
                    orderStateId={orderStateId}
                    isAdditionalPopupOpen={isAdditionalPopupOpen}
                    setIsAdditionalPopupOpen={setIsAdditionalPopupOpen}
                    ordersUpdated={setOrders} />
            case InWarehouseActionTypes.EXPORT:
                return <ScanForExport onCancel={() => closePopup()}
                    orderStateId={orderStateId}
                    preSelectedOrders={orders}
                    ordersUpdated={setOrders} />
            case GenericActionTypes.ADD_TO_GROUP:
                return <AddToGroup onCancel={() => closePopup()}
                    orderGroup={orderGroup}
                    preSelectedOrders={orders}
                    ordersUpdated={setOrders} />
            case InWarehouseActionTypes.PRE_SELECT_LOCATION_AND_ADD_ORDERS:
            case InWarehouseActionTypes.SELECT_LOCATION_AND_ADD_ORDERS:
                return <SelectLocationAndBulkLocate actionType={action}
                    onCancel={() => closePopup()}
                    preSelectedOrders={orders}
                    isAdditionalPopupOpen={isAdditionalPopupOpen}
                    setIsAdditionalPopupOpen={setIsAdditionalPopupOpen}
                    orderStateId={orderStateId}
                    preSelectedLocation={preSelectedLocation}
                    setPreSelectedLocation={setPreSelectedLocation}
                    ordersUpdated={setOrders} />
            case InWarehouseActionTypes.ANNOUNCE:
                return predefinedOrders
                    ? <AnnounceOrder onCancel={() => closePopup()}
                        order={predefinedOrders[0]} />
                    : <></>
            // case InWarehouseActionTypes.SEE_HISTORY:
            //     return historyDialogProps
            //         ? <History category={historyDialogProps.category}
            //                    categoryId={historyDialogProps.categoryId}
            //                    onClose={() => closePopup()}
            //                    type={HistoryPopupType.ALL_ORDER_FIELDS}/>
            //         : <StandardDialogFooter onCancel={() => closePopup()}/>
            case GenericActionTypes.SCAN_FOR_SEARCH:
                return <ScanForSearch onCancel={() => closePopup()}
                    preSelectedOrders={orders}
                    ordersUpdated={setOrders} />
            case GenericActionTypes.CREATE_GROUP:
                return <NewGroup onCancel={() => closePopup()} />
            case GenericActionTypes.SEE_LOCATION_HISTORY:
                return historyDialogProps
                    ? <History category={historyDialogProps.category}
                        categoryId={historyDialogProps.categoryId}
                        onClose={() => closePopup()}
                        type={HistoryPopupType.LOCATION} />
                    : <StandardDialogFooter onCancel={() => closePopup()} exportableOrders={orders} />;
            case GenericActionTypes.SEE_STATUS_HISTORY:
                return historyDialogProps
                    ? <History category={historyDialogProps.category}
                        categoryId={historyDialogProps.categoryId}
                        onClose={() => closePopup()}
                        type={HistoryPopupType.STATUS} />
                    : <StandardDialogFooter onCancel={() => closePopup()} exportableOrders={orders} />;
            case BalanceActionTypes.SEE_BALANCE_HISTORY:
            case BalanceActionTypes.SEE_BONUS_HISTORY:
                return historyDialogProps
                    ? <History category={historyDialogProps.category}
                        categoryId={historyDialogProps.categoryId}
                        onClose={() => closePopup()}
                        type={HistoryPopupType.BALANCE} />
                    : <StandardDialogFooter onCancel={() => closePopup()} exportableOrders={orders} />;
            case GenericActionTypes.SET_DISCOUNT:
                const orderToDiscount = predefinedOrders ? predefinedOrders[0] : undefined;
                return historyDialogProps && orderToDiscount
                    ? <Discount order={orderToDiscount}
                        onClose={() => closePopup()} />
                    : <StandardDialogFooter onCancel={() => closePopup()} exportableOrders={orders} />;
            case WarehouseManagementActionTypes.NEW_WAREHOUSE:
                return <NewWarehouse onCancel={() => closePopup()} />
            case WarehouseManagementActionTypes.NEW_WAREHOUSE_WORKER:
                return <NewWarehouseWorker onCancel={() => closePopup()} />
            case MessagesManagementActionTypes.NEW_MESSAGE:
            case MessagesManagementActionTypes.EDIT_MESSAGE:
                return <NewMessage predefinedMessage={staticDataDialogProps?.staticData} onCancel={() => closePopup()} />
            case StaticDataActionTypes.NEW:
            case StaticDataActionTypes.EDIT:
                return <EditStaticData staticDataDialogProps={staticDataDialogProps} onCancel={() => closePopup()} />
            case StaticDataActionTypes.DELETE:
                return <DeleteStaticData staticDataDialogProps={staticDataDialogProps} onCancel={() => closePopup()} />
            default:
                return <StandardDialogFooter onCancel={() => closePopup()} exportableOrders={orders} />
        }
    }

    if (action && isReady) {
        return (
            <StandardDialog
                sx={{ '& .MuiDialog-container': { visibility: isAdditionalPopupOpen ? 'hidden' : 'unset' } }}
                headerLabel={getHeaderLabel()}
                width={getWidth()}
                open={open}
                key={action + '-' + predefinedOrders?.length}>
                <ActionBlock key={action} />

                <ToastContainer />
            </StandardDialog>
        )
    } else {
        return <></>
    }
}

interface ActionDialogProps {
    open: boolean,
    closePopup: () => void,
    action: string | undefined,
    orderStateId?: number | string;
    predefinedOrders?: OrderModel[];
    orderGroup?: OrderGroupModel;
    historyDialogProps?: { category: string, categoryId: number };
    location?: LocationModel;
    staticDataDialogProps?: StaticDataDialogProps;
    messageSection?: MessageSections;
}
